'use client';

import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Toaster,
  type ToasterProps,
} from 'sonner';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

function getViewportTopOffset(): number {
  return typeof window === 'undefined' ? 0 : (window as any).visualViewport?.offsetTop ?? 0;
}

export default function ToasterProvider() {
  const [viewportTopOffset, setViewportTopOffset] = useState<number>(0);

  useEffect(() => {
    setViewportTopOffset(getViewportTopOffset());
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (!vv) return;
    const handler = () => {
      setViewportTopOffset(getViewportTopOffset());
    };
    vv.addEventListener('resize', handler);
    vv.addEventListener('scroll', handler);
    return () => {
      vv.removeEventListener('resize', handler);
      vv.removeEventListener('scroll', handler);
    };
  }, []);

  // Keep toast at the top-center on mobile as well, regardless of keyboard state
  const position: ToasterProps['position'] = 'top-center';

  const mobileOffset = useMemo(() => {
    // Keep toast visible below mobile browser UI while respecting safe area and dynamic viewport top offset (iOS keyboard/browser chrome)
    const top = Math.round(viewportTopOffset) + 12;
    return { top: `calc(env(safe-area-inset-top, 0px) + ${top}px)` } as const;
  }, [viewportTopOffset]);

  return (
    <Toaster
      position={position}
      closeButton
      duration={4000}
      style={{ zIndex: 2147483647 }}
      offset={12}
      mobileOffset={mobileOffset}
      toastOptions={{
        classNames: {
          toast: 'grid grid-cols-[auto_1fr_auto] items-center !ring-0 !border-0 !pl-3 !pr-0 !py-2 gap-1',
          icon: 'col-start-1 order-1',
          content: 'col-start-2 order-2',
          closeButton: '!static col-start-3 order-3 justify-self-end self-center !ml-auto !shadow-none !border-0 !ring-0 !mt-2.5 text-green-900 !bg-transparent hover:!bg-black/10',
          success: '!bg-green-50 !text-green-900',
          error: '!bg-red-50 !text-red-900',
        },
      }}
      icons={{
        success: <CheckCircleOutlineIcon fontSize="small" className="!text-green-900" />,
        close: <CloseRoundedIcon fontSize="small" className="text-inherit !w-5 !h-5" />,
      }}
    />
  );
}
